import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApiClient } from '../lib/apiClient';
import { Wordmark } from '../components/Logo';
import { formatARS } from '../lib/format';

interface Service {
  id: string;
  name: string;
  price_cents: number;
  duration_min: number;
  active: number;
}

interface UserRow {
  id: string;
  name: string;
  role: 'owner' | 'barber';
  active: number;
}

export default function AppShellPage() {
  const { user, salon, logout } = useAuth();
  const apiClient = useApiClient();
  const [services, setServices] = useState<Service[] | null>(null);
  const [users, setUsers] = useState<UserRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, u] = await Promise.all([
        apiClient<{ services: Service[] }>('/api/services'),
        apiClient<{ users: UserRow[] }>('/api/users'),
      ]);
      if (cancelled) return;
      if (s.ok) setServices(s.data.services);
      if (u.ok) setUsers(u.data.users);
    })();
    return () => { cancelled = true; };
  }, [apiClient]);

  const onboardingDone = (services?.length ?? 0) > 0;

  return (
    <main className="min-h-screen">
      <nav className="border-b border-ink/10 px-6 py-3 flex items-center justify-between">
        <Wordmark className="text-ink" />
        <div className="flex items-center gap-4 text-sm">
          <span className="text-ink/70 hidden sm:inline">
            {salon?.name} · <span className="num">{salon?.slug}</span>
          </span>
          <button onClick={logout} className="text-ink/60 hover:text-danger transition">Salir</button>
        </div>
      </nav>

      <section className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tighter mb-2">
          Hola, {user?.name?.split(' ')[0]}.
        </h1>
        <p className="text-ink/70 mb-10">
          {onboardingDone ? 'Tu Tijera está lista. Cargá tu primer turno o compartí el link de reservas.' : 'Tu Tijera está armada. Falta poco para que la agenda empiece a llenarse sola.'}
        </p>

        {!onboardingDone && (
          <div className="border border-ink/10 rounded-2xl p-6 bg-white">
            <div className="text-xs uppercase tracking-widest text-ash mb-2">Próximo paso</div>
            <h2 className="text-xl font-semibold mb-3">Cargá tus servicios y barberos</h2>
            <p className="text-ink/70 mb-5">
              Antes de empezar a tomar turnos necesitamos saber qué cortes ofrecés y quién los hace. Son 4 minutos.
            </p>
            <Link to="/app/onboarding" className="btn-primary inline-block">Empezar onboarding</Link>
          </div>
        )}

        {onboardingDone && (
          <div className="grid md:grid-cols-2 gap-4">
            <CardLink title="Tu link de reservas" body={`${typeof window !== 'undefined' ? window.location.origin : ''}/r/${salon?.slug}`} cta="Abrir y compartir" to={`/r/${salon?.slug}`} />
            <Card title="Servicios" body={`${services!.length} servicios cargados — desde ${formatARS(Math.min(...services!.map((s) => s.price_cents)))}`} cta="Editar" disabled />
            <Card title="Equipo" body={`${users?.length ?? 0} ${users?.length === 1 ? 'persona' : 'personas'} cortando`} cta="Editar" disabled />
            <CardLink title="Agenda" body="Cargá turnos, marcá asistencias, manejá el día." cta="Abrir agenda" to="/app/agenda" />
          </div>
        )}
      </section>
    </main>
  );
}

function Card({ title, body, cta, disabled }: { title: string; body: string; cta: string; disabled?: boolean }) {
  return (
    <div className="border border-ink/10 rounded-2xl p-5 bg-white">
      <div className="text-xs uppercase tracking-widest text-ash mb-2">{title}</div>
      <p className="text-ink mb-4 break-all">{body}</p>
      <button disabled={disabled} className="text-sm text-ink/60 hover:text-ink disabled:opacity-50">{cta}</button>
    </div>
  );
}

function CardLink({ title, body, cta, to }: { title: string; body: string; cta: string; to: string }) {
  return (
    <Link to={to} className="border border-ink/10 rounded-2xl p-5 bg-white hover:border-ink/30 transition block">
      <div className="text-xs uppercase tracking-widest text-ash mb-2">{title}</div>
      <p className="text-ink mb-4">{body}</p>
      <span className="text-sm text-forest font-medium">{cta} →</span>
    </Link>
  );
}
